import React from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  Select,
  MenuItem,
  FormControl,
  Badge,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import logo from "../assets/logo_scan.png";
import { usePreferences } from "../contexts/PreferencesContext";
import { DASH, getInitials } from "./dashboard/dashboardTokens";
import { AVAILABLE_LANGUAGES } from "../translations";

const SIDEBAR_DARK = "#111111";

const NavItem = ({ item, isActive, onNavigate, requestCount, variant }) => {
  const isRequestsItem = item.path === "/requests";
  const showBadge = isRequestsItem && requestCount > 0;
  const isBroadcast = variant === "broadcast";

  return (
    <Box
      component={RouterLink}
      to={item.path}
      onClick={onNavigate}
      data-tour={item.path}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.75,
        mb: 0.5,
        borderRadius: "16px",
        textDecoration: "none",
        fontSize: "0.875rem",
        fontWeight: 600,
        transition: "all 0.2s ease",
        color: isActive ? "#000" : "#9CA3AF",
        bgcolor: isActive ? "#fff" : "transparent",
        boxShadow: isActive ? "0 10px 15px -3px rgba(0,0,0,0.1)" : "none",
        ...(isBroadcast && !isActive && {
          bgcolor: "rgba(59, 130, 246, 0.08)",
          "& .nav-icon": { color: "#60A5FA" },
        }),
        "&:hover": {
          color: isActive ? "#000" : "#fff",
          bgcolor: isActive ? "#fff" : "rgba(255,255,255,0.1)",
        },
        "& .nav-icon": {
          color: isActive ? "#2563EB" : "inherit",
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          "& .MuiSvgIcon-root": { fontSize: 20 },
        },
      }}
    >
      <Box className="nav-icon">{item.icon}</Box>
      <Box component="span" sx={{ flex: 1, lineHeight: 1.3 }}>
        {item.text}
      </Box>
      {showBadge && (
        <Badge
          badgeContent={requestCount}
          color="error"
          sx={{
            "& .MuiBadge-badge": {
              position: "static",
              transform: "none",
              fontSize: "0.65rem",
              height: 18,
              minWidth: 18,
              ...(isActive && { bgcolor: "#EF4444" }),
            },
          }}
        />
      )}
    </Box>
  );
};

const MobileNavItem = ({ item, isActive, onNavigate, requestCount, variant }) => {
  const isRequestsItem = item.path === "/requests";
  const showBadge = isRequestsItem && requestCount > 0;
  const isBroadcast = variant === "broadcast";

  return (
    <Box
      component={RouterLink}
      to={item.path}
      onClick={onNavigate}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1.5,
        py: 1.25,
        mb: 0.5,
        borderRadius: "8px",
        textDecoration: "none",
        color: isActive ? "#fff" : "text.primary",
        bgcolor: isActive ? "primary.main" : "transparent",
        ...(isBroadcast && !isActive && {
          bgcolor: "rgba(37, 99, 235, 0.06)",
          "& .nav-icon": { color: "primary.main" },
        }),
        "&:hover": {
          bgcolor: isActive ? "primary.dark" : "action.hover",
        },
        "& .nav-icon": {
          color: isActive ? "#fff" : "text.secondary",
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          minWidth: 36,
        },
      }}
    >
      <Box className="nav-icon">{item.icon}</Box>
      <Box component="span" sx={{ flex: 1, fontWeight: 500, fontSize: "0.9rem" }}>
        {item.text}
      </Box>
      {showBadge && (
        <Box
          sx={{
            bgcolor: isActive ? "rgba(255,255,255,0.3)" : "error.main",
            color: "#fff",
            fontSize: "0.7rem",
            fontWeight: "bold",
            px: 1,
            py: 0.25,
            borderRadius: "10px",
            minWidth: 20,
            textAlign: "center",
          }}
        >
          {requestCount}
        </Box>
      )}
    </Box>
  );
};

const Sidebar = ({
  isOpen,
  onClose,
  currentUser,
  menuItems,
  logOut,
  isMobile,
  requestCount = 0,
}) => {
  const location = useLocation();
  const { isDarkMode, toggleTheme, t, language, setLanguage } = usePreferences();

  const languageNames = { SK: "SK", EN: "EN", CZ: "CZ" };
  const handleNavigate = isMobile ? onClose : undefined;

  const getSection = (item) => item.section || (item.path.startsWith("/admin") ? "admin" : "main");

  const mainItems = menuItems.filter((item) => getSection(item) === "main");
  const adminItems = menuItems.filter((item) => getSection(item) === "admin");
  const broadcastItems = menuItems.filter((item) => getSection(item) === "broadcast");

  const sectionLabelSx = {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    px: 2,
    mb: 2,
  };

  const renderNavSection = (items, MobileItem = NavItem, variant) =>
    items.map((item) => {
      const isActive = location.pathname.startsWith(item.path);
      return (
        <MobileItem
          key={item.path}
          item={item}
          isActive={isActive}
          onNavigate={handleNavigate}
          requestCount={requestCount}
          variant={variant}
        />
      );
    });

  // Účet je firma, nie prihlasovacie meno. Od zjednodušenia registrácie sa
  // username rovná e-mailu, takže obe riadky karty ukazovali to isté.
  const accountName = currentUser?.company || currentUser?.username || "Profile";
  const accountEmail = currentUser?.email || currentUser?.username || "";
  const accountInitials = getInitials(accountName) || "?";

  const desktopContent = (
    <Box
      sx={{
        width: 320,
        height: "calc(100vh - 32px)",
        display: "flex",
        flexDirection: "column",
        bgcolor: SIDEBAR_DARK,
        color: "#fff",
        borderRadius: "40px",
        boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
        p: 3,
        overflow: "hidden",
      }}
    >
      {/* Logo */}
      <Box
        component={RouterLink}
        to="/dashboard"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 0.5,
          mb: 5,
          textDecoration: "none",
          color: "inherit",
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Effzone"
          sx={{
            width: 40,
            height: 40,
            objectFit: "contain",
            borderRadius: "50%",
          }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.125rem", lineHeight: 1.1 }}>
            Effzone
          </Typography>
        </Box>
      </Box>

      {/* Navigation */}
      <Box component="nav" sx={{ flex: 1, overflowY: "auto", pr: 0.5, mr: -0.5, display: "flex", flexDirection: "column" }}>
        {broadcastItems.length > 0 && (
          <Box sx={{ mb: 3 }}>
            {renderNavSection(broadcastItems, NavItem, "broadcast")}
          </Box>
        )}

        <Box sx={{ flex: 1 }}>
          <Typography sx={sectionLabelSx}>
            {t("sidebar.menu")}
          </Typography>
          {renderNavSection(mainItems)}

          {adminItems.length > 0 && (
            <>
              <Typography sx={{ ...sectionLabelSx, mt: 2 }}>
                {t("sidebar.adminSection")}
              </Typography>
              {renderNavSection(adminItems)}
            </>
          )}
        </Box>
      </Box>

      {/* Footer controls + user card */}
      <Box sx={{ mt: "auto", pt: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <FormControl
            size="small"
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                bgcolor: "rgba(255,255,255,0.05)",
                color: "#fff",
                fontSize: "0.8rem",
                "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
                "&:hover fieldset": { borderColor: "rgba(255,255,255,0.2)" },
                "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.3)" },
              },
              "& .MuiSelect-icon": { color: "#9CA3AF" },
            }}
          >
            <Select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              displayEmpty
            >
              {AVAILABLE_LANGUAGES.map((lang) => (
                <MenuItem key={lang} value={lang} sx={{ fontSize: "0.875rem" }}>
                  {languageNames[lang]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <IconButton
            onClick={toggleTheme}
            size="small"
            sx={{
              color: "#9CA3AF",
              bgcolor: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "12px",
              width: 40,
              height: 40,
              "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.1)" },
            }}
            title={isDarkMode ? t("common.lightMode") : t("common.darkMode")}
          >
            {isDarkMode ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
          </IconButton>
        </Box>

        <Box
          data-tour="profile-card"
          sx={{
            bgcolor: "rgba(255,255,255,0.05)",
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.1)",
            p: 2,
          }}
        >
          <Box
            component={RouterLink}
            to="/profile"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 1.5,
              textDecoration: "none",
              color: "inherit",
              borderRadius: "12px",
              mx: -0.5,
              px: 0.5,
              py: 0.5,
              "&:hover": { bgcolor: "rgba(255,255,255,0.05)" },
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.14)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: DASH.display,
                fontSize: "0.8125rem",
                fontWeight: 600,
                letterSpacing: "0.01em",
                flexShrink: 0,
              }}
            >
              {accountInitials}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{ fontSize: "0.875rem", fontWeight: 600, lineHeight: 1.3 }}
                noWrap
              >
                {accountName}
              </Typography>
              <Typography
                sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.62)", lineHeight: 1.3 }}
                noWrap
              >
                {accountEmail}
              </Typography>
            </Box>
          </Box>

          <Box
            component="button"
            type="button"
            onClick={logOut}
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              py: 1,
              mt: 0.5,
              border: "none",
              borderTop: "1px solid rgba(255,255,255,0.10)",
              borderRadius: 0,
              bgcolor: "transparent",
              color: "rgba(255,255,255,0.62)",
              fontSize: "0.8125rem",
              fontWeight: 500,
              cursor: "pointer",
              transition: "color .15s ease",
              "&:hover": { color: "#F87171", bgcolor: "transparent" },
            }}
          >
            <ExitToAppIcon sx={{ fontSize: 16 }} />
            {t("sidebar.logout")}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const mobileContent = (
    <Box
      sx={{
        width: 280,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box
          component={RouterLink}
          to="/dashboard"
          onClick={onClose}
          sx={{
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
            color: "inherit",
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Effzone"
            sx={{ width: 36, height: 36, objectFit: "contain", mr: 1.5 }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", lineHeight: 1.2 }}>
              Effzone
            </Typography>
            {currentUser?.company && (
              <Typography variant="body2" noWrap sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                {currentUser.company}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "text.secondary" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 1, py: 1, display: "flex", flexDirection: "column" }}>
        {broadcastItems.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {renderNavSection(broadcastItems, MobileNavItem, "broadcast")}
          </Box>
        )}

        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              px: 1.5,
              mb: 1,
            }}
          >
            {t("sidebar.menu")}
          </Typography>
          {renderNavSection(mainItems, MobileNavItem)}

          {adminItems.length > 0 && (
            <>
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  px: 1.5,
                  mt: 2,
                  mb: 1,
                }}
              >
                {t("sidebar.adminSection")}
              </Typography>
              {renderNavSection(adminItems, MobileNavItem)}
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ mt: "auto", borderTop: 1, borderColor: "divider", p: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.5, mb: 1 }}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <Select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              displayEmpty
              sx={{ fontSize: "0.875rem" }}
            >
              {AVAILABLE_LANGUAGES.map((lang) => (
                <MenuItem key={lang} value={lang} sx={{ fontSize: "0.875rem" }}>
                  {languageNames[lang]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton onClick={toggleTheme} size="small" color="inherit">
            {isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
        </Box>

        <Box
          component={RouterLink}
          to="/profile"
          onClick={onClose}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 1.5,
            py: 1.25,
            mb: 0.5,
            borderRadius: "8px",
            textDecoration: "none",
            color: "text.primary",
            bgcolor: location.pathname === "/profile" ? "primary.main" : "transparent",
            "&:hover": { bgcolor: location.pathname === "/profile" ? "primary.dark" : "action.hover" },
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              bgcolor: "action.hover",
              border: "1px solid",
              borderColor: "divider",
              color: "text.primary",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: DASH.display,
              fontSize: "0.8125rem",
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {accountInitials}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", lineHeight: 1.3 }} noWrap>
              {accountName}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.3 }} noWrap>
              {accountEmail}
            </Typography>
          </Box>
        </Box>

        <Box
          component="button"
          type="button"
          onClick={logOut}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            width: "100%",
            px: 1.5,
            py: 1.25,
            border: "none",
            borderRadius: "8px",
            bgcolor: "transparent",
            color: "text.primary",
            cursor: "pointer",
            textAlign: "left",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <ExitToAppIcon sx={{ color: "text.secondary", fontSize: 22 }} />
          <Typography sx={{ fontWeight: 500 }}>{t("sidebar.logout")}</Typography>
        </Box>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={isOpen}
        onClose={onClose}
        keepMounted={false}
        disableRestoreFocus
        ModalProps={{ keepMounted: false }}
        PaperProps={{ sx: { width: 280 } }}
      >
        {mobileContent}
      </Drawer>
    );
  }

  return desktopContent;
};

export default Sidebar;
